/**
 * LeadHistoryPanel — R48 · right-hand panel inside LeadDialog.
 *
 * Blends every touch on a lead (call · whatsapp · email · manual note ·
 * comment · follow-up) chronologically, and lets the user add a new
 * follow-up entry (with an optional next_followup_at → creates a
 * reminder server-side).
 *
 * Also renders the follow-up log separately at the top so the "next
 * touch date" is prominent.
 */
import { useEffect, useState, useCallback, useRef, memo } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { apiErrorMessage } from "@/lib/apiError";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import {
  Phone, MessageCircle, Mail, StickyNote, CheckSquare, MessageSquare,
  Clock, AlertTriangle, PlusCircle, Loader2, Bell, Eye, Sparkles,
} from "lucide-react";
import MailViewerDialog from "@/components/mail/MailViewerDialog";
import MailComposer from "@/components/mail/MailComposer";
import WhatsappDraftDialog from "@/components/voice/WhatsappDraftDialog";

const CHANNEL_ICON = {
  call:      Phone,
  whatsapp:  MessageCircle,
  sms:       MessageCircle,
  email:     Mail,
  manual:    StickyNote,
  meeting:   CheckSquare,
  followup:  Bell,
  comment:   MessageSquare,
};

const KIND_COLOR = {
  followup:  "border-l-4 border-indigo-500",
  call:      "border-l-4 border-emerald-500",
  whatsapp:  "border-l-4 border-lime-500",
  email:     "border-l-4 border-amber-500",
  comment:   "border-l-4 border-sky-500",
};

function relTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const secs = (Date.now() - d.getTime()) / 1000;
  if (secs < 60) return "just now";
  if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
  if (secs < 86400) return `${Math.floor(secs / 3600)}h ago`;
  const days = Math.floor(secs / 86400);
  if (days < 30) return `${days}d ago`;
  return d.toISOString().slice(0, 10);
}

function toDateLocalInput(iso) {
  if (!iso) return "";
  // Convert ISO datetime to "YYYY-MM-DDTHH:MM" for datetime-local input
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function _LeadHistoryPanel({ leadId, leadName, onFollowupAdded }) {
  const { activeWorkspaceId } = useAuth();
  // R268e · Keep callback fresh through memo without busting equality —
  // ref always points at latest prop, so `addFollowup` reads current
  // even when the outer memo prevents re-render.
  const followupCbRef = useRef(onFollowupAdded);
  useEffect(() => { followupCbRef.current = onFollowupAdded; }, [onFollowupAdded]);
  const [history, setHistory] = useState([]);
  const [followups, setFollowups] = useState([]);
  const [loading, setLoading] = useState(true);
  // Feb 2026 · AI-only voice campaigns auto-draft a WhatsApp follow-up
  // after the call. Surface the pending drafts tied to this lead so
  // the reviewer can approve + send without leaving the lead profile.
  const [waDrafts, setWaDrafts] = useState([]);
  const [waDraftJob, setWaDraftJob] = useState(null);
  // R268k · Feb 2026 — channel filter chips.
  // 'all' means no filter; other values match the event.kind field.
  const [channelFilter, setChannelFilter] = useState("all");

  // Add-followup form state
  const [channel, setChannel] = useState("manual");
  const [note, setNote] = useState("");
  const [outcome, setOutcome] = useState("");
  const [nextAt, setNextAt] = useState("");
  const [busy, setBusy] = useState(false);

  // R66 · Email viewer / composer state (double-click on any email row
  // in the timeline opens the full-message popup).
  const [viewer, setViewer] = useState({ open: false, activityKey: null, replyKey: null });
  const [composer, setComposer] = useState({ open: false, subject: "", to: "", html: "" });

  const load = useCallback(async () => {
    if (!leadId) return;
    setLoading(true);
    try {
      const [h, f] = await Promise.all([
        api.get(`/leads/${leadId}/history/blended?limit=200`),
        api.get(`/leads/${leadId}/followups?limit=50`),
      ]);
      setHistory(h.data?.items || []);
      setFollowups(f.data?.items || []);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Failed to load history"));
    } finally { setLoading(false); }
  }, [leadId]);

  // Pending AI-drafted WhatsApp follow-ups for this lead. Loaded in
  // parallel but independently of the blended timeline so a failure
  // (e.g. workspace not resolved yet) doesn't blank the history.
  const loadWaDrafts = useCallback(async () => {
    if (!leadId || !activeWorkspaceId) return;
    try {
      const { data } = await api.get(
        `/workspaces/${activeWorkspaceId}/leads/${leadId}/voice-campaign-whatsapp-drafts`);
      setWaDrafts(data?.rows || []);
    } catch { setWaDrafts([]); }
  }, [leadId, activeWorkspaceId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadWaDrafts(); }, [loadWaDrafts]);

  const addFollowup = async () => {
    if (!note.trim()) { toast.error("Note is required"); return; }
    setBusy(true);
    try {
      const payload = {
        channel, note: note.trim(),
        outcome: outcome || null,
        next_followup_at: nextAt ? new Date(nextAt).toISOString() : null,
        create_reminder: !!nextAt,
      };
      await api.post(`/leads/${leadId}/followups`, payload);
      toast.success(nextAt ? "Follow-up logged + reminder set" : "Follow-up logged");
      setNote(""); setNextAt(""); setOutcome("");
      await load();
      followupCbRef.current?.();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Add follow-up failed"));
    } finally { setBusy(false); }
  };

  const openMailFromEvent = (e) => {
    if (e.kind !== "email") return;
    const activityKey = e.activity_id || (e.direction === "out" ? e.message_id : null);
    const replyKey = e.reply_id || (e.direction === "in" ? e.message_id : null);
    if (!activityKey && !replyKey) {
      toast.info("This email pre-dates the viewer — full body not stored.");
      return;
    }
    setViewer({ open: true, activityKey, replyKey });
  };

  const openReplyFromMail = (m) => {
    const to = m.direction === "in" ? (m.from || "") : (m.to || "");
    // Strip name prefix "Name <email>" → email only
    const emailOnly = (to.match(/<([^>]+)>/)?.[1]) || to;
    const subj = (m.subject || "").replace(/^(re:\s*)+/i, "");
    const quoted = `<p><br/></p><hr/><blockquote style="border-left:3px solid #e5e7eb;padding-left:12px;color:#6b7280;">${m.body_html || m.html || m.text_preview || ""}</blockquote>`;
    setViewer({ open: false, activityKey: null, replyKey: null });
    setComposer({ open: true, subject: `Re: ${subj}`, to: emailOnly, html: quoted });
  };

  const openForwardFromMail = (m) => {
    const subj = (m.subject || "").replace(/^(fwd?:\s*)+/i, "");
    const quoted = `<p><br/></p><hr/><p><strong>---------- Forwarded message ----------</strong></p><p>From: ${m.from || m.sent_by_name || ""}<br/>Date: ${m.date || m.created_at || ""}<br/>Subject: ${m.subject || ""}<br/>To: ${m.to || m.original_to || ""}</p><blockquote style="border-left:3px solid #e5e7eb;padding-left:12px;color:#6b7280;">${m.body_html || m.html || m.text_preview || ""}</blockquote>`;
    setViewer({ open: false, activityKey: null, replyKey: null });
    setComposer({ open: true, subject: `Fwd: ${subj}`, to: "", html: quoted });
  };

  const nextFollowup = followups.find((f) => f.next_followup_at);
  const isOverdue = nextFollowup?.next_followup_at &&
                     new Date(nextFollowup.next_followup_at) < new Date();

  return (
    <div className="flex flex-col h-full min-h-0" data-testid="lead-history-panel">
      {/* Next follow-up banner */}
      {nextFollowup && (
        <div className={`border p-2 mb-2 text-xs flex items-center gap-2 ${isOverdue ? "bg-rose-50 border-rose-300" : "bg-indigo-50 border-indigo-300"}`}
                data-testid="lead-next-followup-banner">
          {isOverdue ? <AlertTriangle className="w-4 h-4 text-rose-600" /> : <Clock className="w-4 h-4 text-indigo-600" />}
          <div>
            <div className="font-semibold">Next follow-up · {isOverdue ? "OVERDUE" : "scheduled"}</div>
            <div className="text-muted-foreground">
              {new Date(nextFollowup.next_followup_at).toLocaleString()} · {nextFollowup.channel} · {nextFollowup.note?.slice(0, 60)}
            </div>
          </div>
        </div>
      )}

      {/* Feb 2026 · Pending AI-drafted WhatsApp follow-ups from AI-only
          voice campaigns. One-tap review + send so warm leads don't
          cool off while they wait for a human. */}
      {waDrafts.length > 0 && (
        <div className="border border-emerald-500/40 bg-emerald-50/60 p-2 mb-2 space-y-1.5"
             data-testid="lead-wa-drafts-panel">
          <div className="text-[10px] uppercase tracking-widest text-emerald-800 flex items-center gap-1.5">
            <Sparkles className="w-3 h-3" />
            WhatsApp follow-up drafted{waDrafts.length > 1 ? ` · ${waDrafts.length}` : ""}
          </div>
          {waDrafts.map((d) => (
            <div key={d.id}
                 className="border border-emerald-500/30 bg-white p-2 text-xs space-y-1"
                 data-testid={`lead-wa-draft-${d.id}`}>
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-[11px] truncate">
                    {d.campaign_name || "Voice campaign"}
                    {d.whatsapp_draft_interest && (
                      <span className="ml-1.5 inline-block bg-amber-100 text-amber-700 border border-amber-400 text-[9px] px-1 py-[1px]">
                        Interested
                      </span>
                    )}
                  </div>
                  <div className="text-[10px] text-muted-foreground">
                    {d.whatsapp_draft_source === "ai" ? "AI draft" :
                     d.whatsapp_draft_source === "template" ? "Template fallback" :
                     d.whatsapp_draft_source === "human_edited" ? "Edited draft" :
                     "Draft"}
                    {d.whatsapp_draft_at
                      ? ` · ${relTime(d.whatsapp_draft_at)}`
                      : ""}
                  </div>
                </div>
                <Button size="sm"
                        className="rounded-none h-7 px-2 bg-emerald-600 hover:bg-emerald-700 text-white text-[11px]"
                        onClick={() => setWaDraftJob({
                          ...d,
                          lead: { name: leadName, phone: d.resolved_phone } })}
                        data-testid={`lead-wa-review-${d.id}`}>
                  Review & Send
                </Button>
              </div>
              <div className="text-[11px] text-muted-foreground line-clamp-2">
                {d.whatsapp_draft}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add-followup form */}
      <div className="border p-2 mb-2 space-y-1.5 bg-neutral-50" data-testid="lead-add-followup">
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-1">
          <PlusCircle className="w-3 h-3" /> Log a follow-up
        </div>
        <div className="grid grid-cols-2 gap-1.5">
          <div>
            <div className="text-[9px] uppercase tracking-widest text-muted-foreground mb-0.5">Channel</div>
            <Select value={channel} onValueChange={setChannel}>
              <SelectTrigger className="h-8 rounded-none text-xs" data-testid="followup-channel"><SelectValue /></SelectTrigger>
              <SelectContent className="z-[130]">
                <SelectItem value="call" data-testid="followup-channel-call">Call</SelectItem>
                <SelectItem value="whatsapp" data-testid="followup-channel-whatsapp">WhatsApp</SelectItem>
                <SelectItem value="email" data-testid="followup-channel-email">Email</SelectItem>
                <SelectItem value="meeting" data-testid="followup-channel-meeting">Meeting</SelectItem>
                <SelectItem value="sms" data-testid="followup-channel-sms">SMS</SelectItem>
                <SelectItem value="manual" data-testid="followup-channel-note">Note</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <div className="text-[9px] uppercase tracking-widest text-muted-foreground mb-0.5">Outcome (optional)</div>
            <Input value={outcome} onChange={(e) => setOutcome(e.target.value)} placeholder="Positive · No answer · …" className="h-8 rounded-none text-xs" data-testid="followup-outcome" />
          </div>
        </div>
        <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="What happened / notes…" className="rounded-none text-xs" data-testid="followup-note" />
        <div className="flex items-center gap-1.5">
          <Input type="datetime-local" value={nextAt} onChange={(e) => setNextAt(e.target.value)} className="h-8 rounded-none text-xs flex-1" data-testid="followup-next-at" title="Optional — schedules a reminder" />
          <Button size="sm" className="rounded-none h-8" onClick={addFollowup} disabled={busy || !note.trim()} data-testid="followup-submit">
            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : "Log"}
          </Button>
        </div>
        {nextAt && <div className="text-[10px] text-muted-foreground flex items-center gap-1"><Bell className="w-3 h-3" /> A reminder will be created for you.</div>}
      </div>

      {/* Blended timeline */}
      <div className="flex items-center justify-between mb-1 gap-2">
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground">History · all channels</div>
        {!loading && history.length > 0 && (
          <div className="flex items-center gap-1" data-testid="lead-history-filters">
            {[
              { key: "all",      label: "All",   count: history.length },
              { key: "call",     label: "Calls", count: history.filter((e) => e.kind === "call").length },
              { key: "whatsapp", label: "WA",    count: history.filter((e) => e.kind === "whatsapp").length },
              { key: "email",   label: "Email", count: history.filter((e) => e.kind === "email").length },
              { key: "followup",label: "Notes", count: history.filter((e) => e.kind === "followup" || e.kind === "comment").length },
            ].map((c) => (
              <button
                key={c.key}
                data-testid={`lead-history-filter-${c.key}`}
                onClick={() => setChannelFilter(c.key)}
                className={
                  "text-[10px] px-1.5 h-5 rounded-none border transition " +
                  (channelFilter === c.key
                    ? "bg-primary text-primary-foreground border-primary"
                    : "border-border text-muted-foreground hover:text-foreground hover:border-foreground/40")
                }
                title={`${c.label} · ${c.count}`}>
                {c.label}
                <span className="ml-1 opacity-70">{c.count}</span>
              </button>
            ))}
          </div>
        )}
      </div>
      {loading ? (
        // R268e · Feb 2026 — skeleton shimmer so the right column feels
        // populated instantly instead of a lonely "Loading…" line.
        <div className="space-y-1.5 pr-1" data-testid="lead-history-skeleton">
          {[0, 1, 2, 3, 4].map((i) => (
            <div key={i} className="bg-background p-2 border-l-4 border-neutral-200 dark:border-neutral-700 animate-pulse">
              <div className="flex items-center gap-1.5 mb-1.5">
                <div className="w-3 h-3 rounded-full bg-muted" />
                <div className="h-2 w-16 bg-muted rounded" />
                <div className="h-2 w-24 bg-muted/60 rounded ml-auto" />
              </div>
              <div className="h-2 w-full bg-muted/60 rounded mb-1" />
              <div className="h-2 w-4/5 bg-muted/40 rounded" />
            </div>
          ))}
        </div>
      )
       : history.length === 0 ? <div className="text-xs text-muted-foreground border p-4 text-center">No activity yet on this lead.</div> : (
        <div className="flex-1 overflow-y-auto space-y-1.5 pr-1 min-h-0">
          {history.filter((e) => {
            if (channelFilter === "all") return true;
            if (channelFilter === "followup") return e.kind === "followup" || e.kind === "comment";
            return e.kind === channelFilter;
          }).map((e, i) => {
            const Icon = CHANNEL_ICON[e.kind] || StickyNote;
            const colorCls = KIND_COLOR[e.kind] || "border-l-4 border-neutral-300";
            const isEmail = e.kind === "email";
            const hasMailKey = isEmail && (e.activity_id || e.reply_id || e.message_id);
            return (
              <div
                key={`${e.kind}-${i}`}
                className={`bg-background p-2 text-xs ${colorCls} ${hasMailKey ? "cursor-pointer hover:bg-muted/40 transition-colors" : ""}`}
                data-testid={`lead-history-item-${i}`}
                onDoubleClick={() => hasMailKey && openMailFromEvent(e)}
                title={hasMailKey ? "Double-click to open email" : undefined}
              >
                <div className="flex items-center gap-1.5 text-[10px] text-muted-foreground">
                  <Icon className="w-3 h-3" />
                  <span className="uppercase font-semibold">{e.kind}</span>
                  {e.channel && e.channel !== e.kind && <span>· {e.channel}</span>}
                  {e.actor && <span>· {e.actor}</span>}
                  {e.outcome && <span className="border px-1">{e.outcome}</span>}
                  {e.direction && <span className="border px-1">{e.direction}</span>}
                  <span className="ml-auto" title={e.at}>{relTime(e.at)}</span>
                  {hasMailKey && (
                    <button
                      type="button"
                      className="ml-1 p-0.5 hover:bg-muted rounded-none"
                      onClick={(evt) => { evt.stopPropagation(); openMailFromEvent(e); }}
                      title="View full email"
                      data-testid={`lead-history-mail-view-${i}`}
                    >
                      <Eye className="w-3 h-3" />
                    </button>
                  )}
                </div>
                <div className="mt-0.5 whitespace-pre-wrap break-words">{e.text}</div>
                {e.next_followup_at && (
                  <div className="text-[10px] text-indigo-700 mt-0.5 flex items-center gap-1">
                    <Bell className="w-3 h-3" /> Next follow-up · {new Date(e.next_followup_at).toLocaleString()}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* R66 · Full email viewer popup — opens on double-click / view-icon */}
      <MailViewerDialog
        open={viewer.open}
        onOpenChange={(o) => setViewer((s) => ({ ...s, open: o }))}
        activityKey={viewer.activityKey}
        replyKey={viewer.replyKey}
        onReply={openReplyFromMail}
        onForward={openForwardFromMail}
      />

      {/* R66 · Reply / Forward composer — reuses MailComposer with the
          quoted body prefilled from the viewer. */}
      <MailComposer
        open={composer.open}
        onOpenChange={(o) => setComposer((s) => ({ ...s, open: o }))}
        entityType="lead"
        entityId={leadId}
        defaultTo={composer.to}
        defaultSubject={composer.subject}
        defaultHtml={composer.html}
        onSent={() => { setComposer({ open: false, subject: "", to: "", html: "" }); load(); }}
      />

      {/* Feb 2026 · WhatsApp follow-up draft reviewer. */}
      {waDraftJob && (
        <WhatsappDraftDialog
          wsId={activeWorkspaceId} job={waDraftJob}
          leadName={leadName}
          leadPhone={waDraftJob.lead?.phone}
          open
          onClose={(res) => {
            setWaDraftJob(null);
            // Always refresh the pending drafts list — an edit mutates
            // the preview text and a send flips the card off the list.
            loadWaDrafts();
            // Blended history only changes after a send (adds a
            // wa_messages row), so skip the heavier blended reload
            // unless we actually sent something.
            if (res?.sent) load();
          }}
        />
      )}
    </div>
  );
}


// R268e · Feb 2026 — memoise so keystrokes in the sibling form column
// of LeadDialog don't re-render the 200-row timeline. `onFollowupAdded`
// changes reference on every parent render, so we ignore it in the
// equality check (harmless — closure captures the freshest one because
// React batches all state updates before firing effects).
export const LeadHistoryPanel = memo(
  _LeadHistoryPanel,
  (prev, next) => prev.leadId === next.leadId && prev.leadName === next.leadName,
);
